import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Path, RoutePath } from 'src/app/shared/route';
import { LivePromotion } from 'src/app/types/plan';

export interface PromoPopupData {
  promotion: LivePromotion;
}

/**
 * The sale's one-time popup: a near-black hero with the brand orange, the offer, three things the
 * plans include, and a way to the deals. Opened by PromoPopupService; every way of closing it counts,
 * as the shop is marked as having seen it when it opens.
 */
@Component({
  selector: 'app-promo-popup',
  standalone: true,
  imports: [TranslatePipe, MatButtonModule, MatIconModule],
  template: `
    @let promotion = data.promotion;
    @let copy = 'promotion.' + promotion.campaign + '.popup.';
    <div class="relative overflow-hidden rounded-lg bg-white" data-cy="promo-popup">
      <div
        class="relative px-6 pb-6 pt-8 text-white"
        style="background: radial-gradient(circle at 100% 0%, rgb(255 117 0 / 0.35), transparent 55%), #111"
      >
        <button
          mat-icon-button
          class="!absolute right-2 top-2 !text-white"
          (click)="close()"
          [attr.aria-label]="'common.close' | translate"
          data-cy="promo-popup-close"
        >
          <mat-icon>close</mat-icon>
        </button>
        <p class="!m-0 text-xs font-bold uppercase tracking-wider text-[#ff7500]">{{ copy + 'eyebrow' | translate }}</p>
        <h2
          id="promo-popup-title"
          class="!mb-0 !mt-2 !text-5xl !font-extrabold uppercase !leading-none"
          style="
            background: var(--gradients-5, linear-gradient(103deg, #ffac66 0%, #ff7500 41.89%, #e56900 84.71%));
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
          "
          data-cy="promo-popup-headline"
        >
          {{ copy + 'headline' | translate: { percent: promotion.maxPercentOff } }}
        </h2>
        <p class="!mb-0 !mt-2 text-lg">{{ copy + 'subheadline' | translate }}</p>
      </div>

      <div class="px-6 pb-6 pt-5">
        <p class="!m-0 text-sm text-text-2">{{ copy + 'body' | translate }}</p>
        <ul class="!mb-0 mt-4 flex list-none flex-col gap-2 !pl-0 text-sm" data-cy="promo-popup-bullets">
          <li class="flex items-start gap-2">
            <mat-icon class="!text-lg text-[#ff7500]">check</mat-icon>{{ copy + 'feature-recommender' | translate }}
          </li>
          <li class="flex items-start gap-2">
            <mat-icon class="!text-lg text-[#ff7500]">check</mat-icon>{{ copy + 'feature-import' | translate }}
          </li>
          <li class="flex items-start gap-2">
            <mat-icon class="!text-lg text-[#ff7500]">check</mat-icon>{{ copy + 'feature-analytics' | translate }}
          </li>
        </ul>
        <div class="mt-6 flex justify-end gap-2">
          <button mat-button (click)="close()" data-cy="promo-popup-later">{{ copy + 'later' | translate }}</button>
          <button mat-flat-button cdkFocusInitial (click)="seeDeals()" data-cy="promo-popup-cta">
            {{ copy + 'cta' | translate }} →
          </button>
        </div>
      </div>
    </div>
  `,
})
export class PromoPopupComponent {
  readonly data = inject<PromoPopupData>(MAT_DIALOG_DATA);

  private readonly dialogRef = inject(MatDialogRef<PromoPopupComponent>);
  private readonly router = inject(Router);

  close() {
    this.dialogRef.close();
  }

  seeDeals() {
    this.dialogRef.close();
    this.router.navigate(RoutePath.getPath(Path['plans']).fullPath);
  }
}
